// Parsing pasted or typed lyrics into sections, for previews and for the paste flow. The
// server's SongTextParser does the real work when the song is saved; this only has to agree
// with it on the basics: blank lines separate sections, "[Header]" names one, chords are
// inline in brackets or on a line of their own above the words.
const LABEL_WORDS = /^(verse|chorus|bridge|refrain|pre-chorus|prechorus|tag|intro|outro|interlude|ending|coda)\b/i;
const CHORD_TOKEN = /^[A-G][#b]?(m|maj|min|dim|aug|sus|add|M)?\d*(\([^)]*\))?(\/[A-G][#b]?)?$/;

const stripChords = (line) => line.replace(/\[[^\]]*\]/g, "").replace(/\s+/g, " ").trim();

/** A line that is only chord names ("G  C  G"), as people paste them above the words. */
export const looksLikeChordLine = (line) => {
  const tokens = (line || "").trim().split(/\s+/).filter(Boolean);
  return tokens.length > 0 && tokens.every((token) => CHORD_TOKEN.test(token));
};

/**
 * Sections of a text: a "[Header]" line or a leading "Chorus"/"Verse 2" word names a block;
 * unnamed blocks become "Verse n". Each section carries its plain lines, how many chords
 * it has and the first few of them.
 */
export function parseSections(text) {
  const blocks = (text || "")
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  let verse = 0;
  return blocks.map((block, index) => {
    const raw = block.split("\n").map((line) => line.trim()).filter(Boolean);
    let label = null;
    const bracketed = raw[0]?.match(/^\[([^\]]+)\]$/);
    if (bracketed) label = bracketed[1].trim();
    else if (raw[0] && LABEL_WORDS.test(raw[0]) && raw[0].length <= 24) label = raw[0].replace(/[:\-–]\s*$/, "").trim();
    const body = label ? raw.slice(1) : raw;
    if (!label) label = `Verse ${++verse}`;
    const chords = [];
    const lines = [];
    for (const line of body) {
      if (looksLikeChordLine(line)) {
        chords.push(...line.split(/\s+/));
        continue;
      }
      chords.push(...(line.match(/\[([^\]]*)\]/g) || []).map((marker) => marker.slice(1, -1)).filter(Boolean));
      lines.push(stripChords(line));
    }
    return {
      id: `s${index}`,
      label,
      lines,
      chordCount: chords.length,
      chords: chords.slice(0, 3).join(" "),
      preview: lines.join(" ").slice(0, 78),
      lineCount: `${lines.length} ${lines.length === 1 ? "line" : "lines"}`,
    };
  });
}

/** Sections back as text in the app's format, with "[Label]" headers (chords are kept as pasted). */
export function sectionsToText(text) {
  const blocks = (text || "")
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  let verse = 0;
  return blocks
    .map((block) => {
      const raw = block.split("\n").map((line) => line.replace(/\s+$/, "")).filter((line) => line.trim());
      const first = raw[0]?.trim() || "";
      if (/^\[[^\]]+\]$/.test(first)) return raw.join("\n");
      if (LABEL_WORDS.test(first) && first.length <= 24) {
        return [`[${first.replace(/[:\-–]\s*$/, "").trim()}]`, ...raw.slice(1)].join("\n");
      }
      return [`[Verse ${++verse}]`, ...raw].join("\n");
    })
    .join("\n\n");
}
